'use client';
import React, { useState } from 'react';
import { Plus, Search, Edit2, Globe, Eye, Pause, CheckCircle, Copy, Archive, Star, ChevronDown, Clock, Users, Tag, X } from 'lucide-react';

interface Activity {
  id: string;
  title: string;
  category: string;
  type: string;
  status: 'published' | 'draft' | 'paused' | 'archived';
  entryFee: string;
  prize: string;
  players: number;
  maxPlayers: number;
  regions: string[];
  startDate: string;
  featured: boolean;
}

const mockActivities: Activity[] = [
  { id: 'ACT-001', title: 'Daily Trivia Championship', category: 'Knowledge', type: 'Quiz', status: 'published', entryFee: '$2', prize: '$500', players: 1284, maxPlayers: 2000, regions: ['Global'], startDate: 'Aug 8, 2026', featured: true },
  { id: 'ACT-002', title: 'Speed Coding Hackathon', category: 'Professional Skills', type: 'Challenge', status: 'published', entryFee: '$10', prize: '$2,000', players: 342, maxPlayers: 500, regions: ['USA', 'EU', 'India'], startDate: 'Aug 10, 2026', featured: true },
  { id: 'ACT-003', title: 'Logo Design Contest', category: 'Creative', type: 'Competition', status: 'draft', entryFee: 'Free', prize: '$300', players: 0, maxPlayers: 200, regions: ['Global'], startDate: 'Aug 15, 2026', featured: false },
  { id: 'ACT-004', title: 'Math Speed Race', category: 'Knowledge', type: 'Game', status: 'paused', entryFee: '$1', prize: '$100', players: 89, maxPlayers: 500, regions: ['India', 'UK'], startDate: 'Aug 5, 2026', featured: false },
  { id: 'ACT-005', title: 'Photography Challenge', category: 'Creative', type: 'Competition', status: 'published', entryFee: '$5', prize: '$800', players: 156, maxPlayers: 300, regions: ['Global'], startDate: 'Aug 8, 2026', featured: false },
  { id: 'ACT-006', title: 'Business Pitch Arena', category: 'Entrepreneurship', type: 'Challenge', status: 'archived', entryFee: '$15', prize: '$5,000', players: 48, maxPlayers: 100, regions: ['USA', 'UK'], startDate: 'Jul 20, 2026', featured: false },
];

const statusConfig = {
  published: { label: 'Published', color: '#10B981', bg: 'rgba(16,185,129,0.12)' },
  draft: { label: 'Draft', color: '#64748B', bg: 'rgba(100,116,139,0.12)' },
  paused: { label: 'Paused', color: '#F59E0B', bg: 'rgba(245,158,11,0.12)' },
  archived: { label: 'Archived', color: '#F43F5E', bg: 'rgba(244,63,94,0.12)' },
};

interface CreateModalProps { onClose: () => void; }
function CreateActivityModal({ onClose }: CreateModalProps) {
  const [form, setForm] = useState({ title: '', category: '', type: 'Quiz', entryFee: '', prize: '', maxPlayers: '', regions: 'Global', description: '' });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.7)' }}>
      <div className="w-full max-w-lg rounded-2xl overflow-hidden" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
        <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: 'var(--border)' }}>
          <h3 className="text-base font-semibold text-foreground">Create New Activity</h3>
          <button onClick={onClose}><X size={18} style={{ color: 'var(--muted-foreground)' }} /></button>
        </div>
        <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
          <div>
            <label className="text-xs font-semibold mb-1 block" style={{ color: 'var(--muted-foreground)' }}>Activity Title</label>
            <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
              placeholder="Enter activity title..."
              className="w-full px-3 py-2 text-sm rounded-lg outline-none"
              style={{ background: 'var(--muted)', border: '1px solid var(--border)', color: 'var(--foreground)' }} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold mb-1 block" style={{ color: 'var(--muted-foreground)' }}>Category</label>
              <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}
                className="w-full px-3 py-2 text-sm rounded-lg outline-none appearance-none"
                style={{ background: 'var(--muted)', border: '1px solid var(--border)', color: 'var(--foreground)' }}>
                <option value="">Select category</option>
                <option>Knowledge</option><option>Professional Skills</option>
                <option>Creative</option><option>Casual Games</option>
                <option>Entrepreneurship</option><option>Fitness</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold mb-1 block" style={{ color: 'var(--muted-foreground)' }}>Activity Type</label>
              <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}
                className="w-full px-3 py-2 text-sm rounded-lg outline-none appearance-none"
                style={{ background: 'var(--muted)', border: '1px solid var(--border)', color: 'var(--foreground)' }}>
                <option>Quiz</option><option>Challenge</option><option>Game</option>
                <option>Competition</option><option>Tournament</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold mb-1 block" style={{ color: 'var(--muted-foreground)' }}>Entry Fee</label>
              <input value={form.entryFee} onChange={e => setForm({ ...form, entryFee: e.target.value })}
                placeholder="$0 for free"
                className="w-full px-3 py-2 text-sm rounded-lg outline-none"
                style={{ background: 'var(--muted)', border: '1px solid var(--border)', color: 'var(--foreground)' }} />
            </div>
            <div>
              <label className="text-xs font-semibold mb-1 block" style={{ color: 'var(--muted-foreground)' }}>Prize Pool</label>
              <input value={form.prize} onChange={e => setForm({ ...form, prize: e.target.value })}
                placeholder="e.g. $500"
                className="w-full px-3 py-2 text-sm rounded-lg outline-none"
                style={{ background: 'var(--muted)', border: '1px solid var(--border)', color: 'var(--foreground)' }} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold mb-1 block" style={{ color: 'var(--muted-foreground)' }}>Max Players</label>
              <input value={form.maxPlayers} onChange={e => setForm({ ...form, maxPlayers: e.target.value })}
                placeholder="e.g. 500"
                className="w-full px-3 py-2 text-sm rounded-lg outline-none"
                style={{ background: 'var(--muted)', border: '1px solid var(--border)', color: 'var(--foreground)' }} />
            </div>
            <div>
              <label className="text-xs font-semibold mb-1 block" style={{ color: 'var(--muted-foreground)' }}>Region Eligibility</label>
              <select value={form.regions} onChange={e => setForm({ ...form, regions: e.target.value })}
                className="w-full px-3 py-2 text-sm rounded-lg outline-none appearance-none"
                style={{ background: 'var(--muted)', border: '1px solid var(--border)', color: 'var(--foreground)' }}>
                <option>Global</option><option>USA Only</option><option>EU Only</option>
                <option>India Only</option><option>USA + EU</option><option>Custom</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold mb-1 block" style={{ color: 'var(--muted-foreground)' }}>Description</label>
            <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
              rows={3} placeholder="Activity description and rules..."
              className="w-full px-3 py-2 text-sm rounded-lg outline-none resize-none"
              style={{ background: 'var(--muted)', border: '1px solid var(--border)', color: 'var(--foreground)' }} />
          </div>
        </div>
        <div className="flex items-center gap-3 px-5 py-4 border-t" style={{ borderColor: 'var(--border)' }}>
          <button onClick={onClose} className="btn-ghost text-sm py-2 px-4 flex-1">Cancel</button>
          <button className="btn-primary text-sm py-2 px-4 flex-1">Save as Draft</button>
          <button className="btn-accent text-sm py-2 px-4 flex-1">Publish Now</button>
        </div>
      </div>
    </div>
  );
}

export default function AdminActivityManagement() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCreate, setShowCreate] = useState(false);

  const filtered = mockActivities.filter((a) => {
    const matchSearch = !search || a.title.toLowerCase().includes(search.toLowerCase()) || a.category.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'all' || a.status === statusFilter;
    return matchSearch && matchStatus;
  });

  return (
    <div className="space-y-5">
      {showCreate && <CreateActivityModal onClose={() => setShowCreate(false)} />}

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Activity Management</h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--muted-foreground)' }}>Create, edit, publish and manage all platform activities</p>
        </div>
        <button onClick={() => setShowCreate(true)} className="btn-primary text-sm py-2 px-4">
          <Plus size={15} /> New Activity
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Total Activities', value: '1,284', color: '#7C3AED' },
          { label: 'Published', value: '892', color: '#10B981' },
          { label: 'Drafts', value: '234', color: '#64748B' },
          { label: 'Paused', value: '158', color: '#F59E0B' },
        ].map((s) => (
          <div key={s.label} className="rounded-xl p-4" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
            <p className="text-xl font-bold" style={{ color: s.color }}>{s.value}</p>
            <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>{s.label}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 p-4 rounded-xl" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
        <div className="relative flex-1 min-w-48">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--muted-foreground)' }} />
          <input type="text" placeholder="Search activities..." value={search} onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-sm rounded-lg outline-none"
            style={{ background: 'var(--muted)', border: '1px solid var(--border)', color: 'var(--foreground)' }} />
        </div>
        <div className="relative">
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            className="appearance-none pl-3 pr-8 py-2 text-sm rounded-lg outline-none cursor-pointer"
            style={{ background: 'var(--muted)', border: '1px solid var(--border)', color: 'var(--foreground)' }}>
            <option value="all">All Status</option>
            <option value="published">Published</option>
            <option value="draft">Draft</option>
            <option value="paused">Paused</option>
            <option value="archived">Archived</option>
          </select>
          <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'var(--muted-foreground)' }} />
        </div>
      </div>

      {/* Activity Cards */}
      <div className="space-y-3">
        {filtered.map((activity) => {
          const sc = statusConfig[activity.status];
          const fillPct = Math.round((activity.players / activity.maxPlayers) * 100);
          return (
            <div key={activity.id} className="rounded-xl p-4 card-hover"
              style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: 'rgba(124,58,237,0.15)' }}>
                  <Tag size={18} style={{ color: '#A78BFA' }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-sm font-semibold text-foreground">{activity.title}</h3>
                        {activity.featured && (
                          <span className="flex items-center gap-0.5 text-xs px-1.5 py-0.5 rounded-full font-semibold"
                            style={{ background: 'rgba(245,158,11,0.15)', color: '#F59E0B' }}>
                            <Star size={9} fill="#F59E0B" /> Featured
                          </span>
                        )}
                        <span className="text-xs px-2 py-0.5 rounded-full font-semibold"
                          style={{ background: sc.bg, color: sc.color }}>{sc.label}</span>
                      </div>
                      <div className="flex items-center gap-3 mt-1 flex-wrap">
                        <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{activity.id}</span>
                        <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{activity.category} · {activity.type}</span>
                        <span className="flex items-center gap-1 text-xs" style={{ color: 'var(--muted-foreground)' }}>
                          <Globe size={10} />{activity.regions.join(', ')}
                        </span>
                        <span className="flex items-center gap-1 text-xs" style={{ color: 'var(--muted-foreground)' }}>
                          <Clock size={10} />{activity.startDate}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <div className="text-right">
                        <p className="text-xs font-semibold" style={{ color: '#10B981' }}>{activity.prize}</p>
                        <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Entry: {activity.entryFee}</p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 mt-3">
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                          <Users size={10} className="inline mr-1" />{activity.players.toLocaleString()} / {activity.maxPlayers.toLocaleString()} players
                        </span>
                        <span className="text-xs font-semibold" style={{ color: '#A78BFA' }}>{fillPct}%</span>
                      </div>
                      <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--border)' }}>
                        <div className="h-full rounded-full" style={{ width: `${fillPct}%`, background: 'linear-gradient(90deg, #7C3AED, #06B6D4)' }} />
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {[
                        { icon: Eye, label: 'View', color: '#A78BFA' },
                        { icon: Edit2, label: 'Edit', color: '#06B6D4' },
                        { icon: Globe, label: 'Regions', color: '#10B981' },
                        { icon: activity.status === 'published' ? Pause : CheckCircle, label: activity.status === 'published' ? 'Pause' : 'Publish', color: activity.status === 'published' ? '#F59E0B' : '#10B981' },
                        { icon: Copy, label: 'Duplicate', color: '#64748B' },
                        { icon: Archive, label: 'Archive', color: '#F43F5E' },
                      ].map((btn) => (
                        <button key={btn.label} title={btn.label}
                          className="p-1.5 rounded-lg hover:bg-muted transition-colors"
                          style={{ color: btn.color }}>
                          <btn.icon size={14} />
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
